import {
  AccountCircle,
  AlertSpam,
  Assignment,
  CheckCircle,
  EmailLetter,
  People,
  Tag,
  Tickets,
  Time,
} from "@livechat/design-system-icons";
import { IconSource } from "@livechat/design-system-react-components";

import { TRIGGER_NAME } from "routes/automations/workflows/components/triggers/utils";
import { WorkflowConditionType } from "routes/automations/workflows/interfaces";

import { ActionType } from "../actions/types";

export interface ITriggerItem {
  name: string;
  description: string;
  category: TriggerCategory;
  triggerType: WorkflowConditionType;
}

export interface IActionItem {
  name: string;
  description: string;
  category: ActionCategory;
  actionType: ActionType;
}

export const triggerCategories = ["ticket", "assignment", "requester", "spam", "time"] as const;

export type TriggerCategory = (typeof triggerCategories)[number];

export const actionCategories = ["assign", "send", "set", "tag", "spam", "followers"] as const;

export type ActionCategory = (typeof actionCategories)[number];

export const CATEGORY_TITLE: Record<TriggerCategory | ActionCategory, string> = {
  ticket: "Ticket",
  assignment: "Assignment",
  requester: "Requester",
  spam: "Spam",
  time: "Time",
  assign: "Assign",
  send: "Send",
  set: "Set",
  tag: "Tag",
  followers: "Followers",
};

export const CATEGORY_ICON: Record<TriggerCategory | ActionCategory, IconSource> = {
  ticket: Tickets,
  assignment: Assignment,
  requester: AccountCircle,
  spam: AlertSpam,
  time: Time,
  assign: Assignment,
  send: EmailLetter,
  set: CheckCircle,
  tag: Tag,
  followers: People,
};

export const ACTION_NAME: Record<ActionType, string> = {
  [ActionType.Assign]: "Set assignment",
  [ActionType.FeedbackRequest]: "Send feedback request",
  [ActionType.FollowUp]: "Send follow-up",
  [ActionType.Message]: "Send message",
  [ActionType.PrivateNote]: "Send private note",
  [ActionType.Status]: "Set status",
  [ActionType.Priority]: "Set priority",
  [ActionType.AddTags]: "Add tag",
  [ActionType.RemoveTags]: "Remove tag",
  [ActionType.Spam]: "Move to spam",
  [ActionType.Unspam]: "Move to all recent tickets",
  [ActionType.AddFollowers]: "Add follower",
  [ActionType.RemoveFollowers]: "Remove follower",
};

export const ACTION_ICON: Record<ActionType, IconSource> = {
  [ActionType.Assign]: Assignment,
  [ActionType.FeedbackRequest]: EmailLetter,
  [ActionType.FollowUp]: EmailLetter,
  [ActionType.Message]: EmailLetter,
  [ActionType.PrivateNote]: EmailLetter,
  [ActionType.Status]: CheckCircle,
  [ActionType.Priority]: CheckCircle,
  [ActionType.AddTags]: Tag,
  [ActionType.RemoveTags]: Tag,
  [ActionType.Spam]: AlertSpam,
  [ActionType.Unspam]: Tickets,
  [ActionType.AddFollowers]: People,
  [ActionType.RemoveFollowers]: People,
};

export const triggers: ITriggerItem[] = [
  {
    name: TRIGGER_NAME[WorkflowConditionType.TicketCreated],
    description: "Take action when a new ticket is created.",
    category: "ticket",
    triggerType: WorkflowConditionType.TicketCreated,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.TicketChanged],
    description: "Take action when ticket details are updated.",
    category: "ticket",
    triggerType: WorkflowConditionType.TicketChanged,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Source],
    description: "Take action based on where the ticket came from.",
    category: "ticket",
    triggerType: WorkflowConditionType.Source,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Status],
    description: "Take action based on the ticket current status.",
    category: "ticket",
    triggerType: WorkflowConditionType.Status,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.StatusChanged],
    description: "Take action when the ticket status changes.",
    category: "ticket",
    triggerType: WorkflowConditionType.StatusChanged,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Priority],
    description: "Take action based on the ticket priority.",
    category: "ticket",
    triggerType: WorkflowConditionType.Priority,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.PriorityChanged],
    description: "Take action when the ticket priority changes.",
    category: "ticket",
    triggerType: WorkflowConditionType.PriorityChanged,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Rating],
    description: "Take action based on the ticket rating.",
    category: "ticket",
    triggerType: WorkflowConditionType.Rating,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Subject],
    description: "Take action based on the ticket subject.",
    category: "ticket",
    triggerType: WorkflowConditionType.Subject,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.NewRequesterMessage],
    description: "Take action when a requester responds.",
    category: "ticket",
    triggerType: WorkflowConditionType.NewRequesterMessage,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.NewPublicAgentMessage],
    description: "Take action when an agent replies to a requester.",
    category: "ticket",
    triggerType: WorkflowConditionType.NewPublicAgentMessage,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.NewPrivateAgentMessage],
    description: "Take action when an agent adds a private note.",
    category: "ticket",
    triggerType: WorkflowConditionType.NewPrivateAgentMessage,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.NewAnyAgentMessage],
    description: "Take action when an agent sends any message.",
    category: "ticket",
    triggerType: WorkflowConditionType.NewAnyAgentMessage,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Tag],
    description: "Take action based on the ticket tags.",
    category: "ticket",
    triggerType: WorkflowConditionType.Tag,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Language],
    description: "Take action based on the detected ticket language.",
    category: "ticket",
    triggerType: WorkflowConditionType.Language,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.EventCount],
    description: "Take action after a certain number of specified events.",
    category: "ticket",
    triggerType: WorkflowConditionType.EventCount,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Mailbox],
    description: "Take action based on the ticket forwarding address.",
    category: "ticket",
    triggerType: WorkflowConditionType.Mailbox,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Team],
    description: "Take action based on the assigned team.",
    category: "assignment",
    triggerType: WorkflowConditionType.Team,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.TeamChanged],
    description: "Take action when the assigned team changes.",
    category: "assignment",
    triggerType: WorkflowConditionType.TeamChanged,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Agent],
    description: "Take action based on the assigned agent.",
    category: "assignment",
    triggerType: WorkflowConditionType.Agent,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.AgentChanged],
    description: "Take action when the assigned agent changes.",
    category: "assignment",
    triggerType: WorkflowConditionType.AgentChanged,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Follower],
    description: "Take action when a follower is added, removed, or updated.",
    category: "assignment",
    triggerType: WorkflowConditionType.Follower,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Requester],
    description: "Take action based on the requester email.",
    category: "requester",
    triggerType: WorkflowConditionType.Requester,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.IsSpam],
    description: "Take action when a ticket is marked as spam.",
    category: "spam",
    triggerType: WorkflowConditionType.IsSpam,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.IsNotSpam],
    description: "Take action when a ticket is marked as not spam.",
    category: "spam",
    triggerType: WorkflowConditionType.IsNotSpam,
  },
  {
    name: TRIGGER_NAME[WorkflowConditionType.Time],
    description: "Take action after a certain time has passed since the event.",
    category: "time",
    triggerType: WorkflowConditionType.Time,
  },
];

export const actions: IActionItem[] = [
  {
    name: ACTION_NAME[ActionType.Assign],
    description: "Assign the ticket to an agent.",
    category: "assign",
    actionType: ActionType.Assign,
  },
  {
    name: ACTION_NAME[ActionType.FeedbackRequest],
    description: "Send a feedback request to the requester.",
    category: "send",
    actionType: ActionType.FeedbackRequest,
  },
  {
    name: ACTION_NAME[ActionType.FollowUp],
    description: "Send a follow-up message to the requester.",
    category: "send",
    actionType: ActionType.FollowUp,
  },
  {
    name: ACTION_NAME[ActionType.Message],
    description: "Send a custom message to the requester.",
    category: "send",
    actionType: ActionType.Message,
  },
  {
    name: ACTION_NAME[ActionType.PrivateNote],
    description: "Add a private note for internal use.",
    category: "send",
    actionType: ActionType.PrivateNote,
  },
  {
    name: ACTION_NAME[ActionType.Status],
    description: "Set the ticket status.",
    category: "set",
    actionType: ActionType.Status,
  },
  {
    name: ACTION_NAME[ActionType.Priority],
    description: "Set the ticket priority level.",
    category: "set",
    actionType: ActionType.Priority,
  },
  {
    name: ACTION_NAME[ActionType.AddTags],
    description: "Add a tag to the ticket.",
    category: "tag",
    actionType: ActionType.AddTags,
  },
  {
    name: ACTION_NAME[ActionType.RemoveTags],
    description: "Remove a tag from the ticket.",
    category: "tag",
    actionType: ActionType.RemoveTags,
  },
  {
    name: ACTION_NAME[ActionType.Spam],
    description: "Move the ticket to the Spam folder.",
    category: "spam",
    actionType: ActionType.Spam,
  },
  {
    name: ACTION_NAME[ActionType.Unspam],
    description: "Move the ticket to the recent tickets view.",
    category: "spam",
    actionType: ActionType.Unspam,
  },
  {
    name: ACTION_NAME[ActionType.AddFollowers],
    description: "Add a follower to the ticket.",
    category: "followers",
    actionType: ActionType.AddFollowers,
  },
  {
    name: ACTION_NAME[ActionType.RemoveFollowers],
    description: "Remove a follower from the ticket.",
    category: "followers",
    actionType: ActionType.RemoveFollowers,
  },
];
